import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useRoomController } from '../room/RoomCamera';
import { useRoomKeys, toggleFullscreen } from '../room/RoomHud';
import { Brief } from '../command/CommandView';
import { stationKey } from '../room/stations';
import { staticOnly } from '../three/EcosystemFrame';
import SiteTexture from './SiteTexture';
import '../room/room.css';

// A public page as a world, on request. The page renders exactly as before
// — same markup, same copy, same ids, normal document flow, page black
// behind it. Nothing is drawn while a visitor reads (2026-10-22: the
// scroll-driven canvas behind the copy read as background noise). A quiet
// chip (bottom left, `E`) opens Explore: the page's world (data/worlds)
// mounts over the copy at the station of the section you were reading —
// one station per section, a set piece at each — with orbit / pan / zoom,
// cruise (C), 1–9 fly between stations, Esc (or the chip) back to the
// page at the nearest station, which unmounts the canvas again.
//
// Reduced motion, small screens, no WebGL and the prerender
// (`navigator.webdriver`) get the page alone, without the chip.
//
// The page's ground is `SiteTexture` (the login screen's texture, page-wide,
// moving in place) — rendered for everyone, prerender included; it is CSS.
// Pages without a world (the legal documents) stay plain.
const WorldStage = lazy(() => import('./WorldStage'));
const LEAD = 0.3; // a station is "reached" when its section is 30% down the viewport

const live = () => typeof window !== 'undefined' && !staticOnly() && !navigator.webdriver;

function findSection(root, at) {
  if (!root || !at) return null;
  if (at.startsWith('#')) return root.querySelector(at);
  if (at === 'header') return root.querySelector('header.cs-store-hero, header');
  if (at === 'footer') return root.querySelector('footer');
  const m = /^section:(\d+)$/.exec(at);
  if (m) return root.querySelectorAll('section.cs-store-section, section.ak-section')[Number(m[1]) - 1] || null;
  return root.querySelector(at);
}

function WorldLayer({ world }) {
  const ctl = useRoomController();
  const location = useLocation();
  const [found, setFound] = useState([]);       // stations whose section exists, in order
  const anchors = useRef([]);                    // scrollY where each station is reached
  const [active, setActive] = useState(0);
  const [explore, setExplore] = useState(false);
  const [ready, setReady] = useState(false);
  const [help, setHelp] = useState(false);
  const rootRef = useRef(null);

  // Resolve sections → stations; measure their scroll anchors.
  const measure = useCallback(() => {
    const root = document.getElementById('root');
    const vh = window.innerHeight || 800;
    const list = []; const ys = [];
    for (const s of world.stations) {
      const el = findSection(root, s.at);
      if (!el) continue;
      const top = el.getBoundingClientRect().top + window.scrollY;
      list.push({ ...s, el, heading: (el.querySelector('h1, h2, h3')?.textContent || s.label).trim() });
      ys.push(Math.max(0, top - vh * LEAD));
    }
    anchors.current = ys;
    setFound((cur) => (cur.length === list.length && cur.every((c, i) => c.id === list[i].id) ? cur : list));
  }, [world]);
  useEffect(() => {
    measure();
    const t1 = setTimeout(measure, 400); const t2 = setTimeout(measure, 1500);
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [measure, location.pathname]);

  // Scroll → the station being read (no canvas involved; Explore opens there).
  useEffect(() => {
    const onScroll = () => {
      const ys = anchors.current; const n = ys.length;
      if (n < 2) return;
      const y = window.scrollY;
      let k = 0;
      while (k < n - 2 && y >= ys[k + 1]) k += 1;
      const span = Math.max(1, ys[k + 1] - ys[k]);
      const u = Math.max(0, Math.min(n - 1, k + (y - ys[k]) / span));
      const near = Math.round(u);
      setActive((cur) => (cur === near ? cur : near));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [found]);

  useEffect(() => () => { document.body.classList.remove('pw-explore'); }, []);

  // The camera opens at the section being read when Explore is entered
  // (frozen then — flying between stations must not re-home it).
  const [start, setStart] = useState(null);
  const enterExplore = useCallback(() => {
    setStart(found[active]?.pose || null);
    setExplore(true);
    document.body.classList.add('pw-explore');
    ctl.setMode('orbit');
  }, [ctl, found, active]);
  const leaveExplore = useCallback(() => {
    if (!explore) return false;
    const near = ctl.nearest(found) || found[active] || null;
    setExplore(false); setHelp(false); setReady(false);
    ctl.setCruise(false);
    document.body.classList.remove('pw-explore');
    if (near?.el) {
      const vh = window.innerHeight || 800;
      window.scrollTo({ top: Math.max(0, near.el.getBoundingClientRect().top + window.scrollY - vh * LEAD + 1), behavior: 'auto' });
    }
    return true;
  }, [explore, ctl, found, active]);
  const fly = useCallback((s) => { if (!explore) enterExplore(); ctl.flyTo(s.pose, 850); setActive(found.indexOf(s)); }, [explore, enterExplore, ctl, found]);
  // Route change while exploring: back to the page.
  useEffect(() => { if (explore) leaveExplore(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // `E` on the page enters Explore (the room keys take over once inside).
  useEffect(() => {
    if (explore || !found.length) return undefined;
    const onKey = (e) => {
      const t = e.target; if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'e' || e.key === 'E') enterExplore();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [explore, found, enterExplore]);

  useRoomKeys({
    stations: explore ? found : [], onStation: fly, ctl: explore ? ctl : null,
    onEscape: () => leaveExplore(), onExit: () => {}, rootRef, enabled: explore,
    onHelp: (v) => { if (!explore) return false; if (v === 'toggle') { setHelp((h) => !h); return true; } if (v === false) { let was = false; setHelp((h) => { was = h; return false; }); return was; } return false; },
  });

  const card = useMemo(() => {
    const s = found[active];
    return s ? { title: s.heading, subtitle: `station ${stationKey(found, active)} · ${s.label}`, tone: 'off', rows: [] } : null;
  }, [found, active]);

  return (
    <>
      {explore && found.length > 0 && (
        <div ref={rootRef} className={`pw-canvas ${ready ? 'is-ready' : ''}`} aria-hidden="true">
          <Suspense fallback={null}>
            <WorldStage world={{ ...world, stations: found }} ctl={ctl} start={start} active={document.visibilityState !== 'hidden'} onReady={() => setReady(true)} />
          </Suspense>
        </div>
      )}
      {found.length > 0 && (
        <div className="pw-bar" role="toolbar" aria-label="Explore this page in 3D">
          {explore && <button type="button" className={`room-chip ${ctl.cruise ? 'is-active' : ''}`} onClick={() => ctl.setCruise(!ctl.cruise)} aria-pressed={ctl.cruise} title="Cruise: W A S D move · Q E sink rise · ⇧ faster (C)"><kbd>C</kbd>{ctl.cruise ? 'cruising' : 'cruise'}</button>}
          {explore && <button type="button" className="room-chip" onClick={() => ctl.home()} title="Home view (R)"><kbd>R</kbd>home</button>}
          {explore && <button type="button" className="room-chip" onClick={() => toggleFullscreen(document.documentElement)} title="Fullscreen (F)"><kbd>F</kbd>fullscreen</button>}
          {explore && <button type="button" className={`room-chip ${help ? 'is-active' : ''}`} onClick={() => setHelp((h) => !h)} title="Keys (?)"><kbd>?</kbd>keys</button>}
          {explore
            ? <button type="button" className="room-chip is-exit" onClick={leaveExplore} title="Back to the page (Esc)">Back to the page</button>
            : <button type="button" className="room-chip pw-enter" onClick={enterExplore} title="Walk through this page's world: orbit, cruise, fly between sections (E)"><kbd>E</kbd>Explore in 3D</button>}
        </div>
      )}
      {ready && explore && (
        <div className="pw-stations" role="group" aria-label="Stations">
          {found.slice(0, 9).map((s, i) => <button key={s.id} type="button" className={`room-chip ${active === i ? 'is-active' : ''}`} onClick={() => fly(s)}><kbd>{stationKey(found, i)}</kbd>{s.label}</button>)}
        </div>
      )}
      {ready && explore && card && <div className="pw-brief"><Brief card={card} isDefault hint="drag to orbit · wheel to zoom · Esc back to the page" /></div>}
      {ready && explore && help && (
        <div className="room-help pw-help" role="dialog" aria-label="Keys">
          <div className="room-help-head"><strong>Moving around</strong><button type="button" className="cmd-inspector-x" onClick={() => setHelp(false)} aria-label="Close">×</button></div>
          <dl>
            {[['drag', 'orbit'], ['right-drag', 'pan'], ['wheel', 'zoom'], ['1 – 9', 'fly to a section'], ['C', 'cruise on / off'], ['W A S D', 'move (cruise)'], ['Q / E', 'sink / rise'], ['⇧', 'faster'], ['R', 'home'], ['F', 'fullscreen'], ['Esc', 'back to the page']].map(([k, v]) => <React.Fragment key={k}><dt><kbd>{k}</kbd></dt><dd>{v}</dd></React.Fragment>)}
          </dl>
        </div>
      )}
    </>
  );
}

export default function PageWorld({ world, children }) {
  const [on] = useState(() => live());
  return (
    <>
      {world && <SiteTexture />}
      {children}
      {on && world && createPortal(<WorldLayer world={world} />, document.body)}
    </>
  );
}
